/**
 * VroegKeuze — het keuzeblok na het SMS-bericht "onze chauffeur loopt voor"
 * (plan 2026-10-12). SMS kan niet antwoorden, dus de link in dat bericht
 * opent deze pagina met `?vroeg=ophalen|afleveren`. Drie knoppen, dezelfde
 * drie uitkomsten als het WhatsApp-antwoord: eerder is goed, hou de afspraak,
 * of ik ben er niet en het kleed ligt/mag ergens.
 */
import { useState } from 'react'
import { stuurVroegAkkoord } from '../api'

interface Props {
  token: string
  kant: 'ophalen' | 'afleveren'
  onKlaar: () => void
}

export function VroegKeuze({ token, kant, onKlaar }: Props) {
  const [stand, setStand] = useState<'ja' | 'nee' | 'plek' | null>(null)
  const [plek, setPlek] = useState('')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const [klaar, setKlaar] = useState<string | null>(null)

  const aflever = kant === 'afleveren'
  const verstuur = async (keuze: 'ja' | 'nee' | 'plek') => {
    if (keuze === 'plek' && !plek.trim()) {
      setStand('plek')
      return
    }
    setBezig(true)
    setFout(null)
    const r = await stuurVroegAkkoord({ token, kant, stand: keuze, plek: keuze === 'plek' ? plek.trim() : undefined })
    setBezig(false)
    if (!r.success) {
      setFout(r.error?.message || 'Doorgeven is niet gelukt. Bel ons gerust op 088 299 9277.')
      return
    }
    setKlaar(
      keuze === 'ja'
        ? 'Dank je, de chauffeur komt eerder langs.'
        : keuze === 'nee'
          ? 'Dank je, we houden ons aan het afgesproken tijdvak.'
          : `Dank je, we ${aflever ? 'leggen het kleed neer' : 'halen het kleed op'} zoals je aangaf.`
    )
    onKlaar()
  }

  if (klaar) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
        <p className="font-semibold">{klaar}</p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
      <p className="font-semibold text-amber-900">
        Onze chauffeur loopt voor op schema en kan {aflever ? 'je vloerkleed eerder bezorgen' : 'je vloerkleed eerder ophalen'}.
      </p>
      <p className="text-sm text-amber-900/80">
        Past dat? Of ben je er niet en {aflever ? 'mag het kleed ergens neergelegd worden' : 'ligt het kleed ergens klaar'}? Zonder bericht houden we ons aan het afgesproken tijdvak.
      </p>
      <div className="grid gap-2">
        <button type="button" disabled={bezig} onClick={() => void verstuur('ja')}
          className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-white font-semibold disabled:opacity-50">
          Eerder is goed
        </button>
        <button type="button" disabled={bezig} onClick={() => void verstuur('nee')}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-semibold text-gray-800 disabled:opacity-50">
          Hou je aan de afspraak
        </button>
        <button type="button" disabled={bezig} onClick={() => setStand('plek')}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-semibold text-gray-800 disabled:opacity-50">
          Ik ben er niet, het kleed {aflever ? 'mag' : 'ligt'}…
        </button>
      </div>
      {stand === 'plek' && (
        <div className="space-y-2">
          <input
            type="text"
            value={plek}
            onChange={(e) => setPlek(e.target.value)}
            placeholder={aflever ? 'bijvoorbeeld: in de schuur, achter het hek' : 'bijvoorbeeld: onder de carport'}
            className="w-full rounded-xl border border-gray-300 px-3 py-2"
            maxLength={200}
          />
          <button type="button" disabled={bezig || !plek.trim()} onClick={() => void verstuur('plek')}
            className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-white font-semibold disabled:opacity-50">
            Doorgeven
          </button>
        </div>
      )}
      {fout && <p className="text-sm text-red-700">{fout}</p>}
    </div>
  )
}
